import { Box, Card, Skeleton, Typography } from '@mui/material'
import { AssignmentAnalyticsMetrics } from '../types'

interface MetricCardProps {
  loading: boolean
  label: string
  value: string
  sublabel?: string
  valueColor?: string
}

function MetricCard({
  loading,
  label,
  value,
  sublabel,
  valueColor,
}: MetricCardProps) {
  return (
    <Card sx={{ p: 3, flex: 1, minWidth: 0 }}>
      {loading ? (
        <>
          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" height={40} />
          <Skeleton variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {label}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: valueColor ?? 'text.primary',
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>
          {sublabel && (
            <Typography variant="caption" color="text.secondary">
              {sublabel}
            </Typography>
          )}
        </>
      )}
    </Card>
  )
}

interface SummaryMetricsProps {
  loading: boolean
  metrics: AssignmentAnalyticsMetrics | null
}

export default function SummaryMetrics({
  loading,
  metrics,
}: SummaryMetricsProps) {
  let pct = metrics?.avgCompletionPercent ?? 0
  let atRisk = metrics?.atRiskCount ?? 0

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      <MetricCard
        loading={loading}
        label="Total Assignments"
        value={String(metrics?.totalAssignments ?? 0)}
        sublabel="for selected service"
      />
      <MetricCard
        loading={loading}
        label="Avg Completion"
        value={`${pct}%`}
        sublabel="across active assignments in selected service"
        valueColor={
          pct >= 80 ? 'success.main' : pct >= 50 ? 'warning.main' : 'error.main'
        }
      />
      <MetricCard
        loading={loading}
        label="Needs Attention"
        value={String(atRisk)}
        sublabel="at-risk or overdue"
        valueColor={atRisk > 0 ? 'error.main' : 'success.main'}
      />
      <MetricCard
        loading={loading}
        label="Total Enrolled Users"
        value={(metrics?.totalEnrolledUsers ?? 0).toLocaleString()}
        sublabel="unique enrollments"
      />
    </Box>
  )
}
